import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Link2, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type DecisionShareLinkView,
  type DecisionShareMode,
  MAX_DECISION_SHARE_LINKS,
} from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { useConfirmPopover } from '../../../components/ui/confirm-popover';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { copyText } from '../../../lib/clipboard';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { decisionSheetApi, decisionSheetKeys } from '../api';
import { stampDateTime } from '../model';

/** Hạn của link — sprint plan APM-DSX-b: 7 / 30 / 90 ngày / không hạn. */
const EXPIRIES = ['d7', 'd30', 'd90', 'none'] as const;
type Expiry = (typeof EXPIRIES)[number];
const EXPIRY_DAYS: Record<Expiry, number | null> = { d7: 7, d30: 30, d90: 90, none: null };
const MODES: readonly DecisionShareMode[] = ['answer', 'view'];

/** URL server trả có thể là đường tương đối (`/d/<token>`) — chép thì phải đủ origin. */
export function absoluteShareUrl(url: string): string {
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
}

/** Còn mở được: chưa thu hồi, chưa hết hạn. */
export function isLiveShare(link: DecisionShareLinkView, now = Date.now()): boolean {
  if (link.revokedAt) return false;
  return link.expiresAt === null || new Date(link.expiresAt).getTime() > now;
}

/**
 * Phần "Người ngoài qua link" của hộp "Chọn người trả lời" — APM-DSX-b, plan
 * §11.3. Từng là hộp "Chia sẻ" riêng; người dùng 2026-10-07 gộp vào "Chọn người
 * trả lời", vì link "Trả lời" chính là một cách chọn người trả lời.
 *
 * Tạo link (Trả lời / Chỉ xem × hạn), chép link, danh sách link với số lần trả
 * lời, lần cuối và thu hồi. Server chỉ giữ **băm** của token, nên chép lại một
 * link cũ là một lời gọi (`…/url`) chứ không đọc từ danh sách.
 *
 * Link vừa tạo hiện ngay trong hộp kèm nút chép: clipboard có thể bị chặn (mở
 * bằng IP LAN, tab mất focus), và lúc đó người dùng vẫn phải lấy được link.
 */
export function ShareLinksPanel({ sheetId }: { readonly sheetId: string }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const confirm = useConfirmPopover();
  const [mode, setMode] = useState<DecisionShareMode>('answer');
  const [expiry, setExpiry] = useState<Expiry>('d30');
  const [fresh, setFresh] = useState<string | null>(null);

  const links = useQuery({
    queryKey: decisionSheetKeys.shares(sheetId),
    queryFn: () => decisionSheetApi.shares(sheetId),
  });
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: decisionSheetKeys.shares(sheetId) });

  const copy = async (url: string): Promise<boolean> => {
    const ok = await copyText(absoluteShareUrl(url));
    if (!ok) toast.error(t('decisionSheet.share.copyFailed'));
    return ok;
  };

  const create = useMutation({
    mutationFn: () =>
      decisionSheetApi.createShare(sheetId, { mode, expiresInDays: EXPIRY_DAYS[expiry] }),
    onSuccess: async (link) => {
      setFresh(absoluteShareUrl(link.url));
      await refresh();
      if (await copy(link.url)) toast.success(t('decisionSheet.share.created'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const copyExisting = useMutation({
    mutationFn: (shareId: string) => decisionSheetApi.shareUrl(sheetId, shareId),
    onSuccess: async ({ url }) => {
      if (await copy(url)) toast.success(t('decisionSheet.share.copied'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const revoke = useMutation({
    mutationFn: (shareId: string) => decisionSheetApi.revokeShare(sheetId, shareId),
    onSuccess: async () => {
      setFresh(null);
      await refresh();
      toast.success(t('decisionSheet.share.revoked'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const live = (links.data ?? []).filter((link) => isLiveShare(link));
  const full = live.length >= MAX_DECISION_SHARE_LINKS;

  return (
    <div className="flex flex-col gap-3" data-testid="decision-share-panel">
      <p className="text-sm text-muted-foreground">{t('decisionSheet.share.hint')}</p>

      <section className="flex flex-col gap-3 rounded-xl border border-border bg-muted/30 p-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            {t('decisionSheet.share.mode')}
          </span>
          <GlassSegmented
            size="sm"
            ariaLabel={t('decisionSheet.share.mode')}
            value={mode}
            onChange={setMode}
            options={MODES.map((value) => ({
              value,
              label: t(`decisionSheet.share.modes.${value}`),
            }))}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            {t('decisionSheet.share.expiry')}
          </span>
          <GlassSegmented
            size="sm"
            ariaLabel={t('decisionSheet.share.expiry')}
            value={expiry}
            onChange={setExpiry}
            options={EXPIRIES.map((value) => ({
              value,
              label: t(`decisionSheet.share.expiries.${value}`),
            }))}
          />
        </div>
        <Button
          className="self-start"
          disabled={create.isPending || full || links.isPending}
          onClick={() => create.mutate()}
        >
          <Link2 aria-hidden />
          {t('decisionSheet.share.create')}
        </Button>
        {full ? (
          <p role="note" className="text-xs text-rag-amber">
            {t('decisionSheet.share.limit', { max: MAX_DECISION_SHARE_LINKS })}
          </p>
        ) : null}

        {fresh ? (
          <div className="flex min-w-0 flex-col gap-1" data-testid="decision-share-fresh">
            <span className="text-xs font-medium text-muted-foreground">
              {t('decisionSheet.share.newLink')}
            </span>
            <div className="flex min-w-0 items-center gap-2">
              <input
                readOnly
                value={fresh}
                aria-label={t('decisionSheet.share.newLink')}
                onFocus={(event) => event.currentTarget.select()}
                className="h-9 min-w-0 flex-1 rounded-xl border border-input bg-background px-3 font-mono text-xs"
              />
              <Button
                size="icon"
                variant="outline"
                className="size-9 shrink-0"
                aria-label={t('decisionSheet.share.copy')}
                title={t('decisionSheet.share.copy')}
                onClick={() =>
                  void copy(fresh).then(
                    (ok) => ok && toast.success(t('decisionSheet.share.copied')),
                  )
                }
              >
                <Copy aria-hidden className="size-4" />
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="flex flex-col gap-1.5">
        <h3 className="text-xs font-medium text-muted-foreground">
          {t('decisionSheet.share.links')}
        </h3>
        {links.isPending ? (
          <Skeleton className="h-24" />
        ) : links.isError ? (
          <ErrorState message={errorMessage(links.error, t)} onRetry={() => void links.refetch()} />
        ) : links.data.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
            {t('decisionSheet.share.empty')}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-xl border border-border">
            {links.data.map((link) => (
              <ShareRow
                key={link.id}
                link={link}
                busy={copyExisting.isPending || revoke.isPending}
                onCopy={() => copyExisting.mutate(link.id)}
                onRevoke={() =>
                  confirm.ask({
                    message: t('decisionSheet.share.revokeConfirm'),
                    confirmLabel: t('decisionSheet.share.revoke'),
                    onConfirm: () => revoke.mutate(link.id),
                  })
                }
              />
            ))}
          </ul>
        )}
      </section>
      {confirm.popover}
    </div>
  );
}

function ShareRow({
  link,
  busy,
  onCopy,
  onRevoke,
}: {
  readonly link: DecisionShareLinkView;
  readonly busy: boolean;
  readonly onCopy: () => void;
  readonly onRevoke: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const live = isLiveShare(link);
  const facts = [
    link.revokedAt
      ? t('decisionSheet.share.revokedTag')
      : !live
        ? t('decisionSheet.share.expired')
        : link.expiresAt
          ? t('decisionSheet.share.expiresAt', { at: stampDateTime(link.expiresAt) })
          : t('decisionSheet.share.noExpiry'),
    t('decisionSheet.share.answers', { count: link.answerCount }),
    link.lastUsedAt
      ? t('decisionSheet.share.lastUsed', { at: stampDateTime(link.lastUsedAt) })
      : '',
    link.createdBy ? t('decisionSheet.share.createdBy', { name: link.createdBy.name }) : '',
  ].filter((part) => part !== '');

  return (
    <li
      className="flex flex-wrap items-center gap-2 px-3 py-2"
      data-testid={`decision-share-${link.id}`}
    >
      <span className={cn('flex min-w-0 flex-1 flex-col', !live && 'opacity-60')}>
        <span className="text-sm font-medium">{t(`decisionSheet.share.modes.${link.mode}`)}</span>
        <span className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
          {facts.join(' · ')}
        </span>
      </span>
      {live ? (
        <>
          <Button
            size="icon"
            variant="ghost"
            className="size-8"
            aria-label={t('decisionSheet.share.copy')}
            title={t('decisionSheet.share.copy')}
            disabled={busy}
            onClick={onCopy}
          >
            <Copy aria-hidden className="size-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-8 text-destructive"
            aria-label={t('decisionSheet.share.revoke')}
            title={t('decisionSheet.share.revoke')}
            disabled={busy}
            onClick={onRevoke}
          >
            <Trash2 aria-hidden className="size-4" />
          </Button>
        </>
      ) : null}
    </li>
  );
}
